/*
                               (
                              (/(
                              (//(
                              (///(
                             (/////(
                             (//////(                          )
                            (////////(                        (/)
                            (////////(                       (///)
                           (//////////(                      (////)
                           (//////////(                     (//////)
                          (////////////(                    (///////)
                         (/////////////(                   (/////////)
                        (//////////////(                  (///////////)
                        (///////////////(                (/////////////)
                       (////////////////(               (//////////////)
                      (((((((((((((((((((              (((((((((((((((
                     (((((((((((((((((((              ((((((((((((((
                     (((((((((((((((((((            ((((((((((((((
                    ((((((((((((((((((((           (((((((((((((
                    ((((((((((((((((((((          ((((((((((((
                    (((((((((((((((((((         ((((((((((((
                    (((((((((((((((((((        ((((((((((
                    ((((((((((((((((((/      (((((((((
                    ((((((((((((((((((     ((((((((
                    (((((((((((((((((    (((((((
                   ((((((((((((((((((  (((((
                   #################  ##
                   ################  #
                  ################# ##
                 %################  ###
                 ###############(   ####
                ###############      ####
               ###############       ######
              %#############(        (#######
             %#############           #########
            ############(              ##########
           ###########                  #############
          #########                      ##############
        %######

        Powered by Knish.IO: Connecting a Decentralized World

Please visit https://github.com/WishKnish/KnishIO-Client-JS for information.

License: https://github.com/WishKnish/KnishIO-Client-JS/blob/master/LICENSE
*/
import JsSHA from 'jssha'
import {
  chunkSubstr,
  isHex,
  randomString
} from './libraries/strings.js'
import {
  generateBatchId,
  generateBundleHash,
  generateSecret,
  shake256
} from './libraries/crypto.js'
import TokenUnit from './TokenUnit.js'
import WalletCredentialException from './exception/WalletCredentialException.js'
import { ml_kem768 as MlKEM768, ml_kem1024 as MlKEM1024 } from '@noble/post-quantum/ml-kem.js'

const ML_KEM_PARAMS = {
  1024: { kem: MlKEM1024, pkBytes: 1568, skBytes: 3168, ctBytes: 1568 },
  768: { kem: MlKEM768, pkBytes: 1184, skBytes: 2400, ctBytes: 1088 }
}
const DEFAULT_ML_KEM_PARAMETER_SET = 1024

/**
 * Wallet class represents the set of public and private
 * keys to sign Molecules
 */
export default class Wallet {
  /**
   * Class constructor
   *
   * @param {string|null} secret - typically a 2048-character biometric hash
   * @param {string|null} bundle - 64-character hexadecimal user identifier
   * @param {string} token - slug for the token this wallet is intended for
   * @param {string|null} address - hexadecimal public key for the signature of this wallet
   * @param {string|null} position - hexadecimal string used to salt the secret and produce one-time signatures
   * @param {string|null} batchId
   * @param {string|null} characters
   */
  constructor ({
    secret = null,
    bundle = null,
    token = 'USER',
    address = null,
    position = null,
    batchId = null,
    characters = null,
    mlKemParameterSet = DEFAULT_ML_KEM_PARAMETER_SET
  }) {
    const paramSetNum = Number(mlKemParameterSet)
    if (!ML_KEM_PARAMS[paramSetNum]) {
      throw new Error(`KnishIO: unsupported ML-KEM parameter set ${mlKemParameterSet}; expected 1024 or 768.`)
    }
    this.mlKemParameterSet = paramSetNum
    this.token = token
    this.balance = '0'
    this.molecules = {}

    // Empty values
    this.key = null
    this.privkey = null
    this.pubkey = null
    this.tokenUnits = []
    this.tradeRates = {}

    this.address = address
    this.position = position
    this.bundle = bundle
    this.batchId = batchId
    this.characters = characters

    if (secret) {
      // Set bundle from the secret
      this.bundle = this.bundle || generateBundleHash(secret, 'Wallet::constructor')

      // Generate a position for non-shadow wallet if not initialized
      this.position = this.position || Wallet.generatePosition()

      // Key & address initialization
      this.key = Wallet.generateKey({
        secret,
        token: this.token,
        position: this.position
      })
      this.address = this.address || Wallet.generateAddress(this.key)

      // Set characters
      this.characters = this.characters || 'BASE64'

      // Initialize ML-KEM keys
      this.initializeMLKEM()
    }
  }

  /**
   * Creates a new Wallet instance
   *
   * @param {string|null} secret
   * @param {string|null} bundle
   * @param {string} token
   * @param {string|null} batchId
   * @param {string|null} characters
   * @return {Wallet}
   */
  static create ({
    secret = null,
    bundle = null,
    token,
    batchId = null,
    characters = null,
    mlKemParameterSet = DEFAULT_ML_KEM_PARAMETER_SET
  }) {
    let position = null

    // No credentials parameters provided?
    if (!secret && !bundle) {
      throw new WalletCredentialException()
    }

    // Secret, but no bundle?
    if (secret && !bundle) {
      position = Wallet.generatePosition()
      bundle = generateBundleHash(secret, 'Wallet::create')
    }

    // Wallet initialization
    return new Wallet({
      secret,
      bundle,
      token,
      position,
      batchId,
      characters,
      mlKemParameterSet
    })
  }

  /**
   * Determines if the provided string is a bundle hash
   *
   * @param {string} maybeBundleHash
   * @return {boolean}
   */
  static isBundleHash (maybeBundleHash) {
    if (typeof maybeBundleHash !== 'string') {
      return false
    }

    return maybeBundleHash.length === 64 && isHex(maybeBundleHash)
  }

  /**
   * Get formatted token units from the raw data
   *
   * @param unitsData
   * @return {[]}
   */
  static getTokenUnits (unitsData) {
    const result = []
    unitsData.forEach(unitData => {
      result.push(TokenUnit.createFromDB(unitData))
    })
    return result
  }

  /**
   * Generates a private key for the given parameters
   *
   * @param {string} secret
   * @param {string} token
   * @param {string} position
   * @return {string}
   */
  static generateKey ({
    secret,
    token,
    position
  }) {
    if (!secret) {
      throw new WalletCredentialException('Wallet::generateKey() - Secret is required!')
    }
    if (!position) {
      throw new WalletCredentialException('Wallet::generateKey() - Position is required!')
    }

    // Normalize non-hex secret via SHAKE256 (matching Rust/Kotlin behavior)
    const secretHex = isHex(secret) ? secret : shake256(secret, 1024)
    // Normalize non-hex position via SHAKE256
    const positionHex = isHex(position) ? position : shake256(position, 256)

    // Converting secret to bigInt
    const bigIntSecret = BigInt(`0x${ secretHex }`)

    // Adding new position to the user secret to produce the indexed key
    const indexedKey = bigIntSecret + BigInt(`0x${ positionHex }`)

    // Hashing the indexed key to produce the intermediate key
    const intermediateKeySponge = new JsSHA('SHAKE256', 'TEXT')

    intermediateKeySponge.update(indexedKey.toString(16))

    if (token) {
      intermediateKeySponge.update(token)
    }

    // Hashing the intermediate key to produce the private key
    const privateKeySponge = new JsSHA('SHAKE256', 'TEXT')
    privateKeySponge.update(intermediateKeySponge.getHash('HEX', { outputLen: 8192 }))
    return privateKeySponge.getHash('HEX', { outputLen: 8192 })
  }

  /**
   * Generates a wallet address
   *
   * @param {string} key
   * @return {string}
   */
  static generateAddress (key) {
    // Subdivide private key into 16 fragments of 128 characters each
    const keyFragments = chunkSubstr(key, 128)
    // Generating wallet digest
    const digestSponge = new JsSHA('SHAKE256', 'TEXT')

    for (const index in keyFragments) {
      let workingFragment = keyFragments[index]
      for (let fragmentCount = 1; fragmentCount <= 16; fragmentCount++) {
        const workingSponge = new JsSHA('SHAKE256', 'TEXT')
        workingSponge.update(workingFragment)
        workingFragment = workingSponge.getHash('HEX', { outputLen: 512 })
      }

      digestSponge.update(workingFragment)
    }

    // Producing wallet address
    const outputSponge = new JsSHA('SHAKE256', 'TEXT')
    outputSponge.update(digestSponge.getHash('HEX', { outputLen: 8192 }))
    return outputSponge.getHash('HEX', { outputLen: 256 })
  }

  /**
   *
   * @param saltLength
   * @returns {string}
   */
  static generatePosition (saltLength = 64) {
    return randomString(saltLength, 'abcdef0123456789')
  }

  /**
   * Derive an ML-KEM keypair for an arbitrary parameter set from the wallet's key seed.
   *
   * Returns `null` when the wallet holds no key — a secret-less wallet, which is what
   * {@link Molecule.fromJSON} builds for validation context. `generateSecret(null, …)` does NOT
   * throw, so without this the wallet would derive a plausible-looking identity from a bogus seed
   * and fail three layers down at AES-GCM instead of at the missing key. The guard lives here
   * rather than at each call site so a new caller cannot miss it.
   *
   * @param {number} parameterSet - 1024 or 768
   * @return {{pubkey: string, privkey: Uint8Array, params: object}|null}
   */
  _deriveMlKemKeypair (parameterSet) {
    const params = ML_KEM_PARAMS[parameterSet]
    if (!params) {
      throw new Error(`KnishIO: unsupported ML-KEM parameter set ${parameterSet}; expected 1024 or 768.`)
    }
    if (!this.key) {
      return null
    }
    const seedHex = generateSecret(this.key, 128) // 128 hex chars = 64 bytes
    const seed = new Uint8Array(64)
    for (let i = 0; i < 64; i++) {
      seed[i] = parseInt(seedHex.substr(i * 2, 2), 16)
    }
    const { publicKey, secretKey } = params.kem.keygen(seed)
    return {
      pubkey: this.serializeKey(publicKey),
      privkey: secretKey,
      params
    }
  }

  /**
   * Initializes the ML-KEM key pair. Only ever reached from the constructor's `secret` branch,
   * so the derivation cannot come back empty here.
   */
  initializeMLKEM () {
    const derived = this._deriveMlKemKeypair(this.mlKemParameterSet)
    if (!derived) {
      return
    }
    this.pubkey = derived.pubkey
    this.privkey = derived.privkey
  }

  serializeKey (key) {
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(key).toString('base64')
    }
    // String.fromCharCode.apply(null, key) overflows the call stack once the
    // payload exceeds the engine's argument limit (~64K), so convert in chunks.
    let binary = ''
    const CHUNK_SIZE = 0x8000
    for (let i = 0; i < key.length; i += CHUNK_SIZE) {
      binary += String.fromCharCode(...key.subarray(i, i + CHUNK_SIZE))
    }
    return btoa(binary)
  }

  deserializeKey (serializedKey) {
    if (typeof Buffer !== 'undefined') {
      return new Uint8Array(Buffer.from(serializedKey, 'base64'))
    }
    const binaryString = atob(serializedKey)
    return new Uint8Array(binaryString.length).map((_, i) => binaryString.charCodeAt(i))
  }

  /**
   * ML-KEM parameter set implied by a serialized public key's raw byte length. FIPS 203's
   * key lengths are disjoint (1568 bytes → ML-KEM-1024, 1184 bytes → ML-KEM-768), so a stored
   * peer key recovers the parameter set of the session it belongs to without a wire-format
   * change. Used by {@link AuthToken.restore} to resolve a snapshot that predates the field.
   *
   * @param {string|null} pubkey - Base64-serialized ML-KEM public key
   * @return {number|null} 1024, 768, or null when the length matches neither
   */
  static mlKemParameterSetFromPubkey (pubkey) {
    if (!pubkey) {
      return null
    }
    let byteLength
    try {
      byteLength = typeof Buffer !== 'undefined'
        ? Buffer.from(pubkey, 'base64').length
        : atob(pubkey).length
    } catch (e) {
      return null
    }
    for (const [set, params] of Object.entries(ML_KEM_PARAMS)) {
      if (params.pkBytes === byteLength) {
        return Number(set)
      }
    }
    return null
  }

  /**
   * Returns balance as a Number for arithmetic operations.
   * WARNING: Precision loss for values > 2^53.
   *
   * @return {number}
   */
  balanceAsNumber () {
    return Number(this.balance)
  }

  /**
   * Returns balance as a BigInt for precision-safe integer arithmetic.
   * Truncates any fractional component.
   *
   * @return {bigint}
   */
  balanceAsBigInt () {
    const str = String(this.balance)
    const intPart = str.includes('.') ? str.split('.')[0] : str
    return BigInt(intPart || '0')
  }

  /**
   * Sets balance from a BigInt value
   *
   * @param {bigint} value
   */
  setBalanceBigInt (value) {
    this.balance = value.toString()
  }

  /**
   * Sets balance from a Number value, storing as String
   *
   * @param {number} value
   */
  setBalanceNumber (value) {
    this.balance = String(value)
  }

  /**
   *
   * @returns {*[]}
   */
  getTokenUnitsData () {
    const result = []
    this.tokenUnits.forEach(tokenUnit => {
      result.push(tokenUnit.toData())
    })
    return result
  }

  /**
   * Split token units
   *
   * @param {array} units
   * @param remainderWallet
   * @param recipientWallet
   */
  splitUnits (
    units, // Array: token unit IDs
    remainderWallet,
    recipientWallet = null
  ) {
    // No units supplied, nothing to split
    if (units.length === 0) {
      return
    }

    // Init recipient & remainder token units
    const recipientTokenUnits = []
    const remainderTokenUnits = []
    this.tokenUnits.forEach(tokenUnit => {
      if (units.includes(tokenUnit.id)) {
        recipientTokenUnits.push(tokenUnit)
      } else {
        remainderTokenUnits.push(tokenUnit)
      }
    })

    // Reset token units to the sending value
    this.tokenUnits = recipientTokenUnits

    // Set token units to recipient & remainder
    if (recipientWallet !== null) {
      recipientWallet.tokenUnits = recipientTokenUnits
    }
    remainderWallet.tokenUnits = remainderTokenUnits
  }

  /**
   * Split token units across MULTIPLE recipients (WP line 544).
   *
   * The source retains the SENT union (all units leaving), each recipient gets its own subset,
   * and the remainder gets the KEPT units (those not assigned to any recipient). Mirrors
   * splitUnits() but N-way: the source's tokenUnits become the union (not a single recipient's
   * subset) so the source atom carries the full SENT set the validator reads as the authority.
   *
   * @param {array[]} recipientUnitLists - array of unit-id arrays, parallel to recipientWallets
   * @param {Wallet[]} recipientWallets
   * @param {Wallet} remainderWallet
   */
  splitUnitsMulti (
    recipientUnitLists,
    recipientWallets,
    remainderWallet
  ) {
    // The union of all unit ids leaving the source
    const sentIds = new Set()
    recipientUnitLists.forEach(unitList => {
      unitList.forEach(id => sentIds.add(id))
    })

    // Nothing to split (fungible transfer) — leave token units untouched
    if (sentIds.size === 0) {
      return
    }

    // Each recipient gets its own subset of the source's token units
    recipientWallets.forEach((recipientWallet, index) => {
      const ids = recipientUnitLists[index]
      recipientWallet.tokenUnits = this.tokenUnits.filter(tokenUnit => ids.includes(tokenUnit.id))
    })

    // The remainder keeps everything not sent to any recipient
    remainderWallet.tokenUnits = this.tokenUnits.filter(tokenUnit => !sentIds.has(tokenUnit.id))

    // The source carries the SENT union (the ownership authority the validator reads)
    this.tokenUnits = this.tokenUnits.filter(tokenUnit => sentIds.has(tokenUnit.id))
  }

  /**
   * Create a remainder wallet from the source one
   *
   * @param secret
   */
  createRemainder (secret) {
    const remainderWallet = Wallet.create({
      secret,
      token: this.token,
      characters: this.characters
    })
    remainderWallet.initBatchId({
      sourceWallet: this,
      isRemainder: true
    })
    return remainderWallet
  }

  /**
   * @return boolean
   */
  isShadow () {
    return (
      (typeof this.position === 'undefined' || this.position === null) &&
      (typeof this.address === 'undefined' || this.address === null)
    )
  }

  /**
   * Sets up a batch ID - either using the sender's, or a new one
   *
   * @param {Wallet} sourceWallet
   * @param {boolean} isRemainder
   */
  initBatchId ({
    sourceWallet,
    isRemainder = false
  }) {
    if (sourceWallet.batchId) {
      this.batchId = isRemainder ? sourceWallet.batchId : generateBatchId({})
    }
  }

  async encryptMessage (message, recipientPubkey) {
    const messageString = JSON.stringify(message)
    const messageUint8 = new TextEncoder().encode(messageString)
    const deserializedPubkey = this.deserializeKey(recipientPubkey)
    // ML-KEM public keys are exactly 1568 bytes (ML-KEM-1024) or 1184 bytes (ML-KEM-768). A wrong-length key here
    // almost always means the node did not advertise an ML-KEM public key in its auth `key` field (e.g. a validator
    // predating the PQ-transport build). Fail with an actionable message rather than the crypto lib's cryptic
    // `"publicKey" expected Uint8Array of length N, got length=M` assertion.
    const params = ML_KEM_PARAMS[this.mlKemParameterSet]
    if (deserializedPubkey.length !== params.pkBytes) {
      throw new Error(
        `KnishIO: cannot ML-KEM-encrypt — recipient public key is ${deserializedPubkey.length} bytes, ` +
        `expected ${params.pkBytes} (ML-KEM-${this.mlKemParameterSet}). The peer is not running ML-KEM-${this.mlKemParameterSet}; ` +
        'upgrade the peer, or step this client back to the other parameter set.'
      )
    }
    const { cipherText, sharedSecret } = params.kem.encapsulate(deserializedPubkey)
    const encryptedMessage = await this.encryptWithSharedSecret(messageUint8, sharedSecret)
    return {
      cipherText: this.serializeKey(cipherText),
      encryptedMessage: this.serializeKey(encryptedMessage)
    }
  }

  async decryptMessage (encryptedData) {
    const decryptedString = await this._mlkemDecryptToString(encryptedData)
    return decryptedString === null ? null : JSON.parse(decryptedString)
  }

  /**
   * ML-KEM decapsulate + AES-256-GCM decrypt → the RAW decrypted UTF-8 string
   * (no JSON.parse). Shared by {@link decryptMessage} (which JSON.parses the result)
   * and the PQ CipherHash transport ({@link decryptMyMessageML}, which needs the raw
   * response JSON text). PQ-transport Phase E (cycle 163).
   */
  async _mlkemDecryptToString (encryptedData) {
    const { cipherText, encryptedMessage } = encryptedData

    const deserializedCipherText = this.deserializeKey(cipherText)
    const configuredParams = ML_KEM_PARAMS[this.mlKemParameterSet]
    const otherSet = this.mlKemParameterSet === 1024 ? 768 : 1024

    // Inbound is PERMISSIVE: a ciphertext at either parameter set decrypts, provided it is addressed
    // to one of THIS wallet's own ML-KEM identities. The 64-byte seed is parameter-set-independent,
    // so the other identity is derived on demand and its private key is released with this call's
    // scope — never cached on the wallet. Outbound encapsulation stays STRICT (see encryptMessage);
    // reading a 768 record we own downgrades nothing, but encapsulating at 768 would.
    let decapsParams = configuredParams
    let decapsPrivkey = this.privkey
    if (deserializedCipherText.length !== configuredParams.ctBytes) {
      if (deserializedCipherText.length !== ML_KEM_PARAMS[otherSet].ctBytes) {
        console.error(
          `Wallet::decryptMessage() - Ciphertext length mismatch: got ${deserializedCipherText.length}, expected ${configuredParams.ctBytes}`
        )
        return null
      }
      // `null` here means the wallet holds no key to derive from (a secret-less validation
      // wallet); preserve the existing failure observable rather than decapsulating with nothing.
      const derived = this._deriveMlKemKeypair(otherSet)
      if (!derived) {
        console.error(`Wallet::decryptMessage() - cannot derive the ML-KEM-${otherSet} identity: wallet has no key`)
        return null
      }
      decapsParams = derived.params
      decapsPrivkey = derived.privkey
    }

    let sharedSecret
    try {
      sharedSecret = decapsParams.kem.decapsulate(deserializedCipherText, decapsPrivkey)
    } catch (e) {
      console.error('Wallet::decryptMessage() - Decapsulation failed', e)
      console.info('Wallet::decryptMessage() - my public key', this.pubkey)
      return null
    }

    let deserializedEncryptedMessage
    try {
      deserializedEncryptedMessage = this.deserializeKey(encryptedMessage)
    } catch (e) {
      console.warn('Wallet::decryptMessage() - Deserialization failed', e)
      console.info('Wallet::decryptMessage() - my public key', this.pubkey)
      console.info('Wallet::decryptMessage() - our shared secret', sharedSecret)
      return null
    }

    let decryptedUint8
    try {
      decryptedUint8 = await this.decryptWithSharedSecret(deserializedEncryptedMessage, sharedSecret)
    } catch (e) {
      console.warn('Wallet::decryptMessage() - Decryption failed', e)
      console.info('Wallet::decryptMessage() - my public key', this.pubkey)
      console.info('Wallet::decryptMessage() - our shared secret', sharedSecret)
      console.info('Wallet::decryptMessage() - deserialized encrypted message', deserializedEncryptedMessage)
      return null
    }

    try {
      return new TextDecoder().decode(decryptedUint8)
    } catch (e) {
      console.warn('Wallet::decryptMessage() - Decoding failed', e)
      console.info('Wallet::decryptMessage() - my public key', this.pubkey)
      console.info('Wallet::decryptMessage() - our shared secret', sharedSecret)
      console.info('Wallet::decryptMessage() - deserialized encrypted message', deserializedEncryptedMessage)
      console.info('Wallet::decryptMessage() - decrypted Uint8Array', decryptedUint8)
      return null
    }
  }

  /**
   * Multi-recipient map key for a public key: `Base64_standard(SHAKE256(pubkey_utf8, 8 bytes))`
   * — matches the validator's `hash_share` and the other SDKs' `hashShare`/`shortHash`.
   * `shake256(pubkey, 64)` = 64 bits = 8 bytes, hex; hex-decode → standard base64. PQ Phase E.
   */
  hashShare (pubkey) {
    const hex = shake256(pubkey, 64)
    const bytes = Uint8Array.from(hex.match(/.{2}/g).map(b => parseInt(b, 16)))
    return this.serializeKey(bytes)
  }

  /**
   * Post-quantum (ML-KEM) `CipherHash` request envelope: a stringified single-recipient
   * map `{ "<hashShare(recipientPubkey)>": {cipherText, encryptedMessage} }` (object-valued,
   * via {@link encryptMessage}). Matches the Rust validator's CipherHash handler. PQ Phase E.
   */
  async encryptStringML (message, recipientPubkey) {
    const envelope = await this.encryptMessage(message, recipientPubkey)
    return JSON.stringify({ [this.hashShare(recipientPubkey)]: envelope })
  }

  /**
   * Decrypt a `CipherHash` response map addressed to THIS wallet's ML-KEM pubkey
   * (`hashShare(this.pubkey)`) → the RAW decrypted GraphQL response JSON text (NOT JSON.parsed;
   * it replaces the HTTP response body for the normal parser). `null` if no entry / decrypt fails.
   */
  async decryptMyMessageML (map) {
    let envelope = map[this.hashShare(this.pubkey)]
    if (!envelope) {
      // Inbound permissive: try the hash share of the on-demand derived other-set pubkey.
      // A secret-less wallet derives nothing, so the lookup is simply skipped.
      const otherSet = this.mlKemParameterSet === 1024 ? 768 : 1024
      const other = this._deriveMlKemKeypair(otherSet)
      if (other) {
        envelope = map[this.hashShare(other.pubkey)]
      }
    }
    if (!envelope) {
      return null
    }
    return this._mlkemDecryptToString(envelope)
  }

  async encryptWithSharedSecret (message, sharedSecret) {
    const iv = crypto.getRandomValues(new Uint8Array(12))
    const algorithm = { name: 'AES-GCM', iv }

    // Convert the shared secret to a CryptoKey
    const key = await crypto.subtle.importKey(
      'raw',
      sharedSecret,
      { name: 'AES-GCM' },
      false,
      ['encrypt']
    )

    // Encrypt the message
    const encryptedContent = await crypto.subtle.encrypt(
      algorithm,
      key,
      message
    )

    // Combine IV and encrypted content
    const result = new Uint8Array(iv.length + encryptedContent.byteLength)
    result.set(iv)
    result.set(new Uint8Array(encryptedContent), iv.length)

    return result
  }

  /**
   * Decrypts the given message using the shared secret
   * @param encryptedMessage
   * @param sharedSecret
   * @returns {Promise<Uint8Array>}
   */
  async decryptWithSharedSecret (encryptedMessage, sharedSecret) {
    // Extract IV from the encrypted message
    const iv = encryptedMessage.slice(0, 12)
    const algorithm = { name: 'AES-GCM', iv }

    // Convert the shared secret to a CryptoKey
    const key = await crypto.subtle.importKey(
      'raw',
      sharedSecret,
      { name: 'AES-GCM' },
      false,
      ['decrypt']
    )

    // Decrypt the message
    const decryptedContent = await crypto.subtle.decrypt(
      algorithm,
      key,
      encryptedMessage.slice(12)
    )

    return new Uint8Array(decryptedContent)
  }
}
